import React from 'react';
import { Box } from '@mui/material';
import { commandDashboard } from '../commandDashboardTokens';

interface DashboardShellProps {
  header: React.ReactNode;
  /** Customize control under the header (flag `customizable_dashboards`); absent with the flag off. */
  toolbar?: React.ReactNode;
  /** A customized layout's widget grid. When given it replaces the fixed cards below. */
  children?: React.ReactNode;
  kpiStrip?: React.ReactNode;
  chart?: React.ReactNode;
  attention?: React.ReactNode;
  activity?: React.ReactNode;
  jumpTo?: React.ReactNode;
}

/**
 * Outer card + body grid from `design_handoff_store_admin_dashboard/README.md`.
 * A single flat CSS Grid (not nested flex columns) so the mobile breakpoint can
 * reorder cells (attention first) via `grid-template-areas` alone, while the
 * desktop layout keeps chart+activity stacked in a wide left column and
 * attention+jumpTo stacked in a narrower right column.
 */
export const DashboardShell: React.FC<DashboardShellProps> = ({ header, toolbar, children, kpiStrip, chart, attention, activity, jumpTo }) => (
  <Box
    sx={{
      minHeight: '100%',
      p: '28px 24px 40px',
      display: 'flex',
      justifyContent: 'center',
      // Flex default align-items:stretch would force the card (overflow:hidden, for the
      // header's square corners to clip to the card's rounded ones) to exactly match this
      // row's height and clip everything past that — the actual cause of a mobile "page
      // cuts off and won't scroll" bug. flex-start lets the card grow to its content height.
      alignItems: 'flex-start',
    }}
  >
    <Box
      sx={{
        width: '100%',
        maxWidth: '1440px',
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
        bgcolor: commandDashboard.color.outerCard,
        border: `1px solid ${commandDashboard.color.outerCardBorder}`,
        borderRadius: `${commandDashboard.radius.outerCard}px`,
        boxShadow: commandDashboard.shadow.outerCard,
        // `clip`, not `hidden`: still rounds the corners but is not a scroll container,
        // so the edit bar and Widgets panel can stick to the page scroll.
        overflow: 'clip',
      }}
    >
      {header}
      {toolbar}
      {children ?? (
      <>
      {kpiStrip}
      <Box
        sx={{
          p: '20px 24px 22px',
          display: 'grid',
          gap: '20px',
          alignItems: 'stretch',
          // Stacks at the app's shared `md` breakpoint (900px, see `useIsMobile`); moved here from a raw 860px media query.
          gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'minmax(0,1.7fr) minmax(300px,1fr)' },
          gridTemplateAreas: { xs: '"attention" "chart" "activity" "jumpto"', md: '"chart attention" "activity jumpto"' },
        }}
      >
        <Box sx={{ gridArea: 'chart', minWidth: 0, display: 'flex' }}>{chart}</Box>
        <Box sx={{ gridArea: 'attention', minWidth: 0 }}>{attention}</Box>
        <Box sx={{ gridArea: 'activity', minWidth: 0 }}>{activity}</Box>
        <Box sx={{ gridArea: 'jumpto', minWidth: 0 }}>{jumpTo}</Box>
      </Box>
      </>
      )}
    </Box>
  </Box>
);
